import type { ContextFamily } from '@quipex/shared/utils';
import type {
    ActivityIntent,
    ActivityItem,
    ActivityJob,
    ActivityStage,
} from '../activity-journal';
import {
    isActiveActivityJobStatus,
    isCompletedActivityJobStatus,
    isFailedActivityJobStatus,
    isSkippedActivityJobStatus,
    isTerminalActivityJobStatus,
    representativeActivityJob,
} from '../activity-journal/job';
import {
    activitySectionFor,
    hasActivityWarning,
} from '../activity-journal/policy';
import type {
    ActivityIconContext,
    ActivityPrimaryActionView,
    ActivityProgressIndicatorView,
} from './models';

type ProcessingActivityStageTitleState =
    | 'unavailable'
    | 'failed'
    | 'active'
    | 'completed';

type ProcessingActivityStageMetadata = Readonly<
    Record<ProcessingActivityStageTitleState, string>
>;

const PROCESSING_ACTIVITY_STAGE_METADATA = {
    intake: {
        unavailable: 'Document import status unavailable',
        failed: 'Document import failed',
        active: 'Documents importing',
        completed: 'Documents imported',
    },
    classification: {
        unavailable: 'Document classification status unavailable',
        failed: 'Document classification failed',
        active: 'Classifying documents',
        completed: 'Documents classified',
    },
    enrichment: {
        unavailable: 'Document enrichment status unavailable',
        failed: 'Document enrichment failed',
        active: 'Enriching documents',
        completed: 'Documents enriched',
    },
    preview: {
        unavailable: 'Document preview status unavailable',
        failed: 'Document preview failed',
        active: 'Generating document preview',
        completed: 'Document preview generated',
    },
    export: {
        unavailable: 'Document export status unavailable',
        failed: 'Document export failed',
        active: 'Exporting documents',
        completed: 'Documents exported',
    },
    generic: {
        unavailable: 'Document processing status unavailable',
        failed: 'Document processing failed',
        active: 'Processing documents',
        completed: 'Documents processed',
    },
} as const satisfies Readonly<
    Record<ActivityStage, ProcessingActivityStageMetadata>
>;

export function activityStatus(item: ActivityItem): {
    label: string;
    context: ContextFamily;
} {
    const section = activitySectionFor(item);

    if (item.availability === 'unavailable') {
        return { label: 'Unavailable', context: 'destructive' };
    }
    if (item.availability === 'stale') {
        return { label: 'Last known', context: 'secondary' };
    }
    if (item.jobs.some((job) => isFailedActivityJobStatus(job.status))) {
        return { label: 'Needs attention', context: 'destructive' };
    }
    if (hasActivityWarning(item)) {
        return { label: 'Partial', context: 'caution' };
    }
    if (item.jobs.some((job) => isActiveActivityJobStatus(job.status))) {
        return { label: 'Processing', context: 'secondary' };
    }
    if (isActivitySkipped(item))
        return { label: 'Skipped', context: 'caution' };

    switch (section) {
        case 'in-progress':
            return { label: 'Processing', context: 'secondary' };
        case 'needs-attention':
            return { label: 'Needs attention', context: 'destructive' };
        case 'ready':
            return { label: 'Ready', context: 'success' };
        case 'earlier':
            return { label: 'Complete', context: 'secondary' };
    }
}

export function toActivityAction(
    intent: ActivityIntent | null,
    target: string
): ActivityPrimaryActionView | null {
    if (!intent) return null;

    switch (intent.type) {
        case 'view-receipt':
            return {
                type: intent.type,
                label: 'View receipt',
                icon: 'lucideFileText',
                ariaLabel: `View receipt for ${target}`,
            };
        case 'open-preview':
            return {
                type: intent.type,
                label: 'Open preview',
                icon: 'lucideEye',
                ariaLabel: `Open preview for ${target}`,
            };
        case 'download-selected-export':
        case 'download-export':
        case 'download-pack-export':
            return {
                type: intent.type,
                label: 'Download ZIP',
                icon: 'lucideDownload',
                ariaLabel: `Download ZIP for ${target}`,
            };
    }
}

export function processingActivityTitle(
    item: ActivityItem,
    latest: ActivityJob
): string {
    if (latest.stage === 'export' && latest.exportKind === 'pack') {
        if (item.availability === 'unavailable') {
            return 'Pack export status unavailable';
        }
        if (isActivitySkipped(item)) return 'Pack export skipped';
        if (isFailedActivityJobStatus(latest.status)) {
            return 'Pack export failed';
        }
        if (isActiveActivityJobStatus(latest.status)) return 'Exporting pack';
        return 'Pack exported';
    }

    const stageMetadata = PROCESSING_ACTIVITY_STAGE_METADATA[latest.stage];

    if (item.availability === 'unavailable') {
        return stageMetadata.unavailable;
    }

    if (isActivitySkipped(item)) return 'Documents skipped';
    if (latest.warning) return 'Documents partially imported';
    if (isFailedActivityJobStatus(latest.status)) return stageMetadata.failed;
    if (isActiveActivityJobStatus(latest.status)) return stageMetadata.active;

    return stageMetadata.completed;
}

export function activityStageIcon(stage: ActivityStage): string {
    switch (stage) {
        case 'intake':
            return 'lucideImport';
        case 'classification':
            return 'lucideSparkles';
        case 'enrichment':
            return 'lucideSparkles';
        case 'preview':
            return 'lucideEye';
        case 'export':
            return 'lucideUpload';
        case 'generic':
            return 'lucideActivity';
    }
}

export function activityIconContext(
    item: ActivityItem,
    _stage: ActivityStage
): ActivityIconContext {
    if (item.availability === 'unavailable') return { family: 'destructive' };
    if (item.availability === 'stale') return { family: 'secondary' };
    if (item.jobs.some((job) => isFailedActivityJobStatus(job.status))) {
        return { family: 'destructive' };
    }
    if (hasActivityWarning(item)) return { family: 'caution' };
    if (item.jobs.some((job) => isActiveActivityJobStatus(job.status))) {
        return { family: 'secondary', tone: 'muted' };
    }
    if (item.jobs.some((job) => isSkippedActivityJobStatus(job.status)))
        return { family: 'caution' };
    if (
        item.jobs.length > 0 &&
        item.jobs.every((job) => isCompletedActivityJobStatus(job.status))
    ) {
        return { family: 'success' };
    }

    return { family: 'secondary' };
}

export function activityProgress(
    item: ActivityItem,
    representative: ActivityJob,
    title: string,
    options: { readonly countLabel?: string | null } = {}
): ActivityProgressIndicatorView | null {
    if (
        item.availability === 'unavailable' ||
        activitySectionFor(item) !== 'in-progress' ||
        isTerminalActivityJobStatus(representative.status)
    ) {
        return null;
    }

    if (representative.percentComplete === null) {
        return {
            mode: 'indeterminate',
            ariaLabel: `${title} in progress`,
        };
    }

    const progressPercent = Math.round(representative.percentComplete);

    return {
        mode: 'determinate',
        value: progressPercent,
        label: `${progressPercent}%`,
        ...(options.countLabel ? { countLabel: options.countLabel } : {}),
        ariaLabel: `${title}: ${progressPercent}% complete`,
    };
}

export function isActivitySkipped(item: ActivityItem): boolean {
    return isSkippedActivityJobStatus(
        representativeActivityJob(item.jobs).status
    );
}

export function activityUpdatedLabel(timestamp: string, now: number): string {
    const elapsedMinutes = Math.max(
        0,
        Math.floor((now - Date.parse(timestamp)) / 60_000)
    );
    if (elapsedMinutes < 1) return 'just now';
    if (elapsedMinutes < 60) return `${elapsedMinutes} min ago`;

    const elapsedHours = Math.floor(elapsedMinutes / 60);
    if (elapsedHours < 24) {
        return `${elapsedHours} ${elapsedHours === 1 ? 'hour' : 'hours'} ago`;
    }

    return new Date(timestamp).toLocaleDateString();
}
